
/**
 * Changes made:
 * - 2024-03-20: Removed all auction-related functionality
 * - 2024-03-20: Cleaned up imports
 * - 2024-03-20: Simplified component to focus on vehicle valuation
 * - 2025-05-22: Fixed spacing and layout to ensure proper rendering on homepage
 * - 2025-05-24: Fixed z-index and visibility issues to resolve blank content
 * - 2025-05-20: Added padding at the top to prevent navbar overlap with hero content
 * - 2025-08-10: Updated hero background to 'surface' color token (#FCFCFC)
 * - 2025-09-17: Translated hero text to Polish and replaced brand logos with partner logos
 * - 2025-09-20: Complete hero redesign with red background, removed geometric pattern for clean design
 * - 2025-09-27: Added proper top padding (pt-28) to create visual spacing between navbar and valuation form
 * - 2025-09-27: Added BackgroundVector component with #B51821 color positioned on left side of hero section
 * - 2025-09-27: Added RightBackgroundVector component with #E31C25 color positioned on right side of hero section
 * - 2025-09-27: Added BottomRightVector component with #E11C25 color positioned below right vector and aligned with left vector
 * - 2025-09-27: Restructured hero with fixed-width background container and precise vector positioning to match target design
 * - 2025-09-28: Added HeroTextContent component with heading and subtitle text, restructured layout with proper Content container
 * - 2025-10-01: Added mobile optimization using useIsMobile hook - preserves desktop design, adds mobile-friendly vertical layout
 * - 2025-10-02: Changed content container alignment from items-center to items-start to align hero text with top of valuation form
 * - 2025-10-02: Updated mobile version with solid red background, valuation form displayed first followed by text content
 * - 2025-10-02: Added spacing from navbar (pt-32) and rounded container (#383B39) for mobile form to show curves
 * - 2025-10-03: Added tablet detection with useIsTablet hook to provide iPad-optimized layout (768px-1024px)
 * - 2025-10-03: Created tablet-specific vertical layout with larger sizing than mobile, maintains desktop design above 1024px
 */

import { ValuationForm } from "@/components/hero/ValuationForm";
import { BackgroundVector } from "@/components/hero/BackgroundVector";
import { RightBackgroundVector } from "@/components/hero/RightBackgroundVector";
import { BottomRightVector } from "@/components/hero/BottomRightVector";
import { HeroTextContent } from "@/components/hero/HeroTextContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTablet } from "@/hooks/use-tablet";

export const Hero = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Mobile version - solid red background with form first, then text
  if (isMobile) {
    return (
      <div id="hero" className="relative w-full overflow-hidden px-4 flex items-center justify-center min-h-[600px] pt-24 pb-8 bg-[#D81B24]">
        <div className="w-full max-w-[420px] h-auto relative">
          <ValuationForm isMobile={true} />
          
          {/* Text content below */}
          <div className="mt-6">
            <HeroTextContent isMobile={true} />
          </div>
        </div>
      </div>
    );
  }

  // Tablet version (iPad) - vertical layout optimized for tablets
  if (isTablet) {
    return (
      <div id="hero" className="relative w-full overflow-hidden px-6 flex items-center justify-center min-h-[700px] pt-28 pb-8 bg-[#D81B24]">
        <div className="w-full max-w-[600px] h-auto relative flex flex-col items-center">
          <ValuationForm isTablet={true} />
          
          {/* Text content below */}
          <div className="mt-8">
            <HeroTextContent isTablet={true} />
          </div>
        </div>
      </div>
    );
  }

  // Desktop version (unchanged)
  if (!isMobile && !isTablet) {
    return (
      <div id="hero" className="relative w-full overflow-hidden px-4 md:px-8 flex items-center justify-center min-h-[500px] pt-28 pb-8">
        <div className="w-[1357px] h-[539px] relative bg-white rounded-3xl overflow-hidden">
          <BackgroundVector />
          <RightBackgroundVector />
          <BottomRightVector />
          
          <div data-layer="Content" className="Content size- pt-3 inline-flex justify-start items-start gap-40 absolute left-12 top-20 z-10">
            <HeroTextContent />
            <ValuationForm />
          </div>
        </div>
      </div>
    );
  }
};
